import * as React from 'react';
import { ProjectCard } from '../../components/ProjectCard';
import { useConstructionTime, useProjectParameters, useProjectPrice } from './hooks';
import { trackClickPhoneButton, trackClickProjectCard } from './tracking';
import { IProject } from '../../types/buildersProjects';
import { PhoneButtonContainer } from '../PhoneButtonContainer';
import { Responsive, useDeviceType } from '@cian/ui-kit';
import { CallButtonContainer } from '../CallButtonContainer';
import { useApplicationContext } from '../../utils/applicationContext';
import { LabelsContainer } from '../LabelsContainer';

interface IProjectCardContainerProps extends IProject {
  index: number;
}

export const ProjectCardContainer: React.FC<IProjectCardContainerProps> = ({
  index,
  id,
  images,
  name,
  description,
  constructionTime: constructionTimeRaw,
  area,
  floorCount,
  materials,
  startPrice,
  endPrice,
  url,
  company,
  hasTour,
}) => {
  const position = index + 1;
  const parameters = useProjectParameters({ area, floorCount, materials });
  const price = useProjectPrice({ startPrice, endPrice });
  const constructionTime = useConstructionTime(constructionTimeRaw);
  const deviceType = useDeviceType();
  const { config } = useApplicationContext();
  const isTourEnabled = !!config.get('Countryside.BuildersTour.Enabled');

  const handleCardClick = React.useCallback(() => {
    trackClickProjectCard({
      position,
      builderId: company.id,
      builderName: company.name,
      projectId: id,
      projectName: name,
      deviceType,
    });
  }, [position, company.id, company.name, id, name, deviceType]);

  const handleGalleryClick = React.useCallback(() => {
    const target = deviceType === 'desktop' ? '_blank' : undefined;

    window.open(url, target);
  }, [deviceType, url]);

  const handleShowPhone = React.useCallback(() => {
    trackClickPhoneButton({ position, builderId: company.id, deviceType, projectId: id });
  }, [position, company.id, deviceType, id]);

  return (
    <ProjectCard
      images={images}
      name={name}
      description={description}
      parameters={parameters}
      constructionTime={constructionTime}
      price={price}
      url={url}
      builder={company}
      hasTour={hasTour && isTourEnabled}
      phoneButton={
        <>
          <Responsive desktop>
            <PhoneButtonContainer phone={company.phone} onShow={handleShowPhone} />
          </Responsive>
          <Responsive tablet phone>
            <CallButtonContainer builder={company} onCallClick={handleShowPhone} fullWidth />
          </Responsive>
        </>
      }
      onCardClick={handleCardClick}
      onGalleryClick={handleGalleryClick}
      labels={<LabelsContainer builderId={company.id} />}
    />
  );
};
